import { createContext, type FunctionComponent, type PropsWithChildren, useContext, useReducer } from 'react';
import { REGEX_WHITE_SPACE } from '../../constants/regular-expression';
import { useRouterLocation } from '../../hooks/routing/location.hook';
import { useRouteChange } from '../../hooks/routing/route-change.hook';
import { searchBarReducer } from '../../reducers/search-bar/search-bar-reducer';
import type { TypeaheadAction, TypeaheadState } from '../../reducers/search-bar/search-bar-reducer.types';

export const DEFAULT_TYPEAHEAD_STATE: Readonly<TypeaheadState> = Object.freeze({
	value: '',
	displayValue: '',
	currentSearch: '',
	suggestionsLoading: false,
	valueChanged: false,
	inputFocused: false,
	showResults: false,
	showRecent: false,
	showSuggestions: false,
	cursorSection: 'input',
	recentTermsCursorIndex: -1,
	recentTermsRemoveCursorIndex: -1,
	suggestedTermsCursorIndex: -1,
	suggestedProductsCursorIndex: -1,
	recentTerms: [],
	recentTermsRemove: [],
	suggestedTerms: [],
	suggestedProducts: [],
	suggestedProductsColumns: 3,
	renderProducts: false
});

export const SearchBarStateContext = createContext<TypeaheadState>(DEFAULT_TYPEAHEAD_STATE);
export const SearchBarDispatchContext = createContext<React.Dispatch<TypeaheadAction>>(() => {});

export function useSearchBarReducerContext(): [TypeaheadState, React.Dispatch<TypeaheadAction>] {
	const state = useContext(SearchBarStateContext);
	const dispatch = useContext(SearchBarDispatchContext);
	return [state, dispatch];
}

export const SearchBarProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
	const { search } = useRouterLocation();
	const [state, dispatch] = useReducer(searchBarReducer, { value: getUrlSearchTerm(search) }, getInitialState);
	return (
		<SearchBarStateContext.Provider value={state}>
			<SearchBarDispatchContext.Provider value={dispatch}>
				<SearchBarLocationChangeHandler>{children}</SearchBarLocationChangeHandler>
			</SearchBarDispatchContext.Provider>
		</SearchBarStateContext.Provider>
	);
};

const SearchBarLocationChangeHandler: FunctionComponent<PropsWithChildren> = ({ children }) => {
	const dispatch = useContext(SearchBarDispatchContext);
	useRouteChange((location) => dispatch({ type: 'locationChanged', term: getUrlSearchTerm(location.search) }));
	return children;
};

function getUrlSearchTerm(search: string): string {
	return cleanTerm(new URLSearchParams(search).get('term') ?? '');
}

function cleanTerm(term: string): string {
	return term.replace(REGEX_WHITE_SPACE, ' ').trim();
}

function getInitialState(initializerArg: { value: string }): TypeaheadState {
	const { value } = initializerArg;
	return { ...DEFAULT_TYPEAHEAD_STATE, value, displayValue: value, currentSearch: value };
}
